import { useState, useLayoutEffect, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import "./DocumentsPage.scss";
import { useDarkModeCheck } from "../DashboardLayout";
import FilterRow from "../../../component/filter-row/FilterRow";
import {
  documentTypeSelectorList,
  SortOrder,
  tableDocumentsHeader,
} from "../../../service/utils/constants";
import SortableTable, {
  SortSettings,
} from "../../../component/sortable-table/SortableTable";
import Container from "../../../component/container/Container";
import TabsContainer from "../../../component/tabs-container/TabsContainer";
import InsuranceDocuments from "../../../component/insurance-documents/InsuranceDocuments";
import { sortTableFunction } from "../../../service/utils/functions";
import {
  GET_CUSTOMERS,
  GET_DOCUMENT_DATA,
  GET_INSURANCE_DOCUMENT_DATA,
} from "../../../service/graphql/query";
import * as Storage from "../../../service/storage";
import {
  CustomerDataEntry,
  InsuranceDocumentEntry,
} from "../../../service/utils/interfaces";
import {
  customerListFormat,
  useCustomerFilters,
  useRequestGuard,
} from "../../../service/hooks/customerData";
import { documentsDataFormat } from "../../../service/utils/parsers";
import { logout } from "../../../service/graphql/utils";

const DocumentsPage = () => {
  const navigate = useNavigate();
  const { darkMode, userCustomers } = useDarkModeCheck();

  const [headerLine, setHeaderLine] = useState<any[]>([]);
  const [content, setContent] = useState<any[][]>([]);
  const [sortSettings, setSortSettings] = useState<SortSettings>();
  const [searchParam, setSearchParam] = useState("");
  const [reordering, setReordering] = useState(false);

  const [customerList, setCustomerList] = useState<CustomerDataEntry[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>();
  const [sitesPerCustomer, setSitesPerCustomer] = useState<any[]>([]);
  const [selectedSites, setSelectedSites] = useState<any>();
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<any>();

  const [currentTab, setCurrentTab] = useState(0);

  const [width, setWidth] = useState(0);

  const loadCustomerFilters = useCustomerFilters();
  const isNewDocumentsRequest = useRequestGuard();
  // Customer whose sites are loaded into the filter state. Documents wait for
  // it, so no call goes out with the previous customer's sites.
  const [filtersCustomerId, setFiltersCustomerId] = useState<number>();
  const [getCustomers, { data: customersData }] = useLazyQuery(GET_CUSTOMERS);
  const [getDocuments, { data: documentsData }] =
    useLazyQuery(GET_DOCUMENT_DATA);
  const [
    getInsuranceDocuments,
    { data: insuranceDocumentsData, loading: insuranceDocumentsLoading },
  ] = useLazyQuery(GET_INSURANCE_DOCUMENT_DATA);

  useLayoutEffect(() => {
    const width = window.innerWidth;
    setWidth(width);
  }, []);

  useEffect(() => {
    if (!userCustomers) return;
    const user = userCustomers[0];
    if (user) {
      if (user.category === "Tipping") {
        window.history.replaceState(null, "", "/");
        navigate("/dashboard/home");
      }
    } else logout(navigate);
  }, [userCustomers, navigate]);

  useEffect(() => {
    setHeaderLine(tableDocumentsHeader);

    Storage.USER_DATA.get()
      .then(async (obj) => {
        if (obj.length > 0) {
          setCustomerList(obj);
          const filter = await Storage.FILTER_DATA.get();
          const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
          setSelectedCustomer(obj[idx]);
        } else getCustomers({ context: { apiName: "middleware" } });
      })
      .catch((_) => getCustomers({ context: { apiName: "middleware" } }));

    const handleWindowResize = () => {
      const width = window.innerWidth;
      setWidth(width);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [getCustomers]);

  useEffect(() => {
    getInsuranceDocuments({ context: { apiName: "middleware" } });
  }, [getInsuranceDocuments]);

  useEffect(() => {
    if (customersData && customersData.getCustomers) {
      manageCustomersData(customersData);
    }
  }, [customersData]);

  const manageCustomersData = async (customersData: any) => {
    // Sites are loaded later, for the selected customer only.
    const tempCustomerList = customerListFormat(customersData.getCustomers);
    setCustomerList(tempCustomerList);
    const filter = await Storage.FILTER_DATA.get();
    const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
    setSelectedCustomer(tempCustomerList[idx]);
  };

  useEffect(() => {
    if (!selectedCustomer) return;
    let isCurrent = true;

    (async () => {
      const customer = await loadCustomerFilters(selectedCustomer);
      const filter = await Storage.FILTER_DATA.get();
      if (!isCurrent) return;

      const sites = customer.sites ?? [];
      setSitesPerCustomer(sites);
      setSelectedSites(
        sites.filter((_, idx) => filter.siteIndex?.includes(idx))
      );
      setSelectedDocumentTypes([]);
      setFiltersCustomerId(selectedCustomer.value);
    })();

    return () => {
      isCurrent = false;
    };
  }, [selectedCustomer, loadCustomerFilters]);

  useEffect(() => {
    if (
      selectedCustomer &&
      filtersCustomerId === selectedCustomer.value &&
      selectedSites &&
      selectedDocumentTypes
    ) {
      let siteIdList: any[] = [];
      if (selectedSites.length > 0)
        siteIdList = selectedSites.map((el: any) => el.value);
      else siteIdList = sitesPerCustomer.map((el: any) => el.value);

      let doctypeList: any[] = [];
      if (selectedDocumentTypes.length > 0)
        doctypeList = selectedDocumentTypes.map((el: any) => el.label);
      else
        doctypeList = documentTypeSelectorList.map((doctype) => doctype.label);

      const input = {
        siteList: siteIdList,
        documentTypeList: doctypeList,
        documentId: "-99",
      };
      if (!isNewDocumentsRequest(input)) return;
      getDocuments({
        variables: { input },
        context: { apiName: "middleware" },
      });
    }
  }, [
    selectedCustomer,
    filtersCustomerId,
    selectedSites,
    sitesPerCustomer,
    selectedDocumentTypes,
    getDocuments,
    isNewDocumentsRequest,
  ]);

  useEffect(() => {
    if (documentsData && documentsData.getJobIssueDocList) {
      manageDocumentsData(documentsData);
    }
  }, [documentsData, selectedCustomer]);

  const manageDocumentsData = (documentsData: any) => {
    const parsedDocuments = documentsDataFormat(
      documentsData.getJobIssueDocList.documents
    );
    setContent(parsedDocuments);
  };

  const insuranceDocuments: InsuranceDocumentEntry[] =
    insuranceDocumentsData?.getInsuranceDocList ?? [];

  const changeSortAction = (col: number) => {
    setSortSettings({
      column: col,
      order:
        sortSettings?.column === col &&
        sortSettings?.order === SortOrder.Ascending
          ? SortOrder.Descending
          : SortOrder.Ascending,
    });
    setReordering(true);
  };

  const changeSearchParam = (val: string) => {
    setSearchParam(val);
  };

  useEffect(() => {
    if (sortSettings && reordering) {
      setReordering(false);
      const reorderedTable = content.sort((a, b) =>
        sortTableFunction(a, b, sortSettings)
      );
      setContent(reorderedTable);
    }
  }, [sortSettings, reordering, content]);

  const resetTable = () => {
    changeSearchParam("");
    setSortSettings(undefined);
  };

  const renderTabContent = () => [
    <Container darkTheme={darkMode} key="documents-tab">
      <div className="column">
        <SortableTable
          id="document-list"
          width={width}
          header={headerLine}
          data={content}
          standaloneTable
          isAuditTable
          isDocumentTable
          currentSort={sortSettings}
          onSortChanged={changeSortAction}
          currentFilterKey={searchParam}
          onFilterChanged={changeSearchParam}
          rowsPerPage={25}
          darkMode={darkMode}
        />
      </div>
    </Container>,
    <InsuranceDocuments
      key="insurance-documents-tab"
      data={insuranceDocuments}
      loading={insuranceDocumentsLoading}
      darkMode={darkMode}
    />,
  ];

  return (
    <div className={"documents-wrapper" + (darkMode ? " dark" : "")}>
      {/* The filters only drive the Documents tab; on Insurance Documents they stay
          visible but greyed out, since those documents are the same for everyone. */}
      <div
        className={
          "filter-row row" + (currentTab === 0 ? "" : " filters-disabled")
        }
        aria-disabled={currentTab !== 0}
      >
        <div className="filter-row-inner">
          <FilterRow
            width={width}
            customerData={customerList}
            onChangeCustomerData={(val) => {
              resetTable();
              setSelectedCustomer(val);
            }}
            siteData={sitesPerCustomer}
            onChangeSiteData={(val) => {
              resetTable();
              setSelectedSites([...val]);
            }}
            documentTypeData={documentTypeSelectorList}
            onChangeDocumentTypeData={(val) => {
              resetTable();
              setSelectedDocumentTypes([...val]);
            }}
            darkMode={darkMode}
          />
        </div>
      </div>
      <TabsContainer
        darkTheme={darkMode}
        tabList={["Documents", "Insurance Documents"]}
        contentList={renderTabContent()}
        currentTab={currentTab}
        onSwitchTab={(tabIdx) => setCurrentTab(tabIdx)}
      />
    </div>
  );
};

export default DocumentsPage;
